/** Saved external examples, not measured store findings or executable Opportunities. */
import * as z from 'zod';
import { researchEvidenceUseSchema } from '@testfeed/integration-contracts';
import { entitlementStateSchema } from '@testfeed/entitlement-contracts';
const id = z.string().min(1).max(256);
export const researchCardLimits = {
    cards: 3,
    cases: 5,
    contextBytes: 8192,
    researchBytes: 32768,
    inputTokens: 16000,
    outputTokens: 1500,
    refreshMicros: 50000,
    storeMonthMicros: 200000,
    applicationMonthMicros: 20000000,
    refreshesPerMonth: 4,
    cooldownMs: 86400000,
    scanLimit: 25,
};
export const researchCardScopeSchema = z.strictObject({ orgId: id, connectionId: id, shop: id });
export const researchCardFragmentSchema = z.strictObject({
    id,
    kind: z.enum(['store_summary', 'merchant_brief', 'merchant_answer', 'study_memory']),
    revision: id,
    productId: id.nullable(),
    value: z.string().min(1).max(8192),
    limitation: z.string().min(1).max(2000),
});
export const researchCardContextSchema = z.strictObject({
    scope: researchCardScopeSchema,
    fingerprint: id,
    authority: id,
    fragments: z.array(researchCardFragmentSchema).max(30),
    publicTerms: z.array(z.string().min(1).max(200)).max(10),
    termSources: z.array(z.strictObject({ term: id, fragmentId: id, reason: id })).max(100),
    omissions: z.array(z.strictObject({ id, reason: id })).max(100),
});
export const researchCardDecisionSchema = z.strictObject({
    caseId: id,
    applicability: z.enum(['direct', 'transferable', 'not_applicable']),
    fragmentIds: z.array(id).max(10),
    rationale: z.string().min(1).max(1200),
    mechanism: z.string().min(1).max(400),
    limitation: z.string().min(1).max(1000),
    passageRefs: z.array(id).max(10),
    question: z.string().max(300),
});
export const researchCardDecisionsSchema = z.strictObject({ decisions: z.array(researchCardDecisionSchema).max(5) });
export const researchCardSchema = z.strictObject({
    id,
    caseId: id,
    revisionId: id,
    title: z.string().min(1),
    summary: z.string().min(1),
    question: z.string().min(1).max(300),
    limitation: z.string().min(1),
    rationale: z.string().min(1),
    source: z.array(z.strictObject({ title: id, publisher: id, url: z.url() })).min(1),
    evidenceUse: researchEvidenceUseSchema,
});
export const storeResearchCardsSchema = z.strictObject({
    scope: researchCardScopeSchema,
    setId: id.nullable(),
    createdAt: z.string().nullable(),
    state: z.enum([
        'ready',
        'not_started',
        'no_match',
        'insufficient_context',
        'restricted',
        'pending',
        'failed',
        'budget_deferred',
    ]),
    cards: z.array(researchCardSchema).max(3),
});
/** No default model or automatic enablement. Prices are explicit reviewed upper bounds, USD/million tokens. */
export const researchCardPolicySchema = z.strictObject({
    enabled: z.boolean(),
    model: id,
    reasoningEffort: z.enum(['none', 'low', 'medium', 'high']),
    maxInputTokens: z.number().int().min(1).max(researchCardLimits.inputTokens),
    maxOutputTokens: z.number().int().min(1).max(researchCardLimits.outputTokens),
    maxCostMicros: z.number().int().min(1).max(researchCardLimits.refreshMicros),
    inputUsdPerMillion: z.number().positive(),
    outputUsdPerMillion: z.number().positive(),
    priceSource: z.url(),
    priceReviewedAt: z.iso.datetime(),
});
export const researchCardPreviewSchema = z.strictObject({
    context: researchCardContextSchema,
    confirmationHash: id,
    policy: researchCardPolicySchema.nullable(),
    entitlement: z.strictObject({ state: entitlementStateSchema, reasonCode: id }),
    settingsSnapshot: z.string().min(1),
    query: z.string().nullable(),
    reason: z.enum([
        'ready',
        'not_entitled',
        'disabled',
        'insufficient_context',
        'unchanged',
        'pending',
        'cooldown',
        'budget_deferred',
        'index_unavailable',
    ]),
    maxSearchCalls: z.literal(1),
    maxModelCalls: z.literal(1),
    maxProviderCalls: z.literal(3),
    maxInputTokens: z.number().int().min(1).max(researchCardLimits.inputTokens),
    maxOutputTokens: z.number().int().min(1).max(researchCardLimits.outputTokens),
    reservedMicros: z.number().int().nonnegative().nullable(),
    storeRemainingMicros: z.number().int(),
    applicationRemainingMicros: z.number().int(),
    libraryEnabled: z.boolean(),
    indexReadyCount: z.number().int().nonnegative().nullable(),
});
export const researchCardDismissSchema = z.strictObject({ caseId: id, dismissed: z.boolean() });
//# sourceMappingURL=store-research-cards.js.map